import React, { useState, useEffect, useRef } from "react";
import { message, Modal } from "antd";
import {
  ReloadOutlined,
  SaveOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import {
  GetBarcodeTemplates,
  CreateBarcodeTemplate,
  UpdateBarcodeTemplate,
  DeleteBarcodeTemplate,
  SetDefaultBarcodeTemplate,
  GetProducts,
} from "../../Api";
import { PageHeader } from "../../components";
import {
  TemplatesSidebar,
  WorkspaceCanvas,
  PropertiesSidebar,
  PrintConfigModal,
} from "./common";
import { generateCode128SvgPath } from "../../Utils";

function generateCostCode(price: number, alphabet: string): string {
  const cipher = alphabet.toUpperCase().padEnd(10, "X");
  const digits = price.toFixed(0).toString();
  return digits
    .split("")
    .map((d) => {
      const idx = parseInt(d, 10);
      return isNaN(idx) ? d : cipher[idx];
    })
    .join("");
}

interface LabelElement {
  id: string;
  type: "text" | "barcode" | "qrcode" | "logo" | "line";
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize?: number;
  fontWeight?: string;
  text?: string;
  align?: "left" | "center" | "right";
  barcodeFormat?: "code128" | "qr";
  rotation?: number;
}

interface LabelTemplate {
  id?: string;
  name: string;
  width: number;
  height: number;
  elements: LabelElement[];
  is_default?: boolean;
}

export function BarcodeEditor() {
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [activeTemplate, setActiveTemplate] = useState<LabelTemplate>({
    name: "New Label Template",
    width: 50,
    height: 30,
    elements: [
      {
        id: "1",
        type: "text",
        x: 10,
        y: 8,
        w: 160,
        h: 22,
        fontSize: 11,
        fontWeight: "bold",
        text: "{product_name}",
        align: "center",
      },
      {
        id: "2",
        type: "barcode",
        x: 10,
        y: 34,
        w: 160,
        h: 42,
        barcodeFormat: "code128",
        text: "{barcode}",
      },
      {
        id: "3",
        type: "text",
        x: 10,
        y: 82,
        w: 160,
        h: 18,
        fontSize: 9,
        fontWeight: "semibold",
        text: "Rs. {price}",
        align: "center",
      },
    ],
  });

  const [selectedElementId, setSelectedElementId] = useState<string | null>(
    null,
  );
  const [products, setProducts] = useState<any[]>([]);
  const [previewProduct, setPreviewProduct] = useState<any | null>(null);
  const [costCodeAlphabet, setCostCodeAlphabet] =
    useState<string>("COMPUTERSX");
  const [expiryDaysOffset, setExpiryDaysOffset] = useState<number>(365);
  const [gridSnap, setGridSnap] = useState<boolean>(true);
  const [logoBase64, setLogoBase64] = useState<string>("");
  const [zoom, setZoom] = useState<number>(2.0);

  // Printing Configuration
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printQty, setPrintQty] = useState<number>(24);
  const [printCols, setPrintCols] = useState<number>(4);
  const [printMarginLeft, setPrintMarginLeft] = useState<number>(10);
  const [printMarginTop, setPrintMarginTop] = useState<number>(15);
  const [printColGap, setPrintColGap] = useState<number>(5);
  const [printRowGap, setPrintRowGap] = useState<number>(5);

  const canvasRef = useRef<HTMLDivElement>(null);
  const scale = 3.78; // 1mm = 3.78px

  useEffect(() => {
    fetchTemplates();
    fetchProducts();
  }, []);

  const fetchTemplates = async () => {
    const res = await GetBarcodeTemplates();
    if (res.success && res.data) {
      const parsed = res.data.map((t: any) => ({
        ...t,
        elements:
          typeof t.elements === "string" ? JSON.parse(t.elements) : t.elements,
        is_default: !!t.is_default,
      }));
      setTemplates(parsed);

      const defaultTpl = parsed.find((t: any) => t.is_default);
      if (defaultTpl) {
        setActiveTemplate(defaultTpl);
      }
    }
  };

  const fetchProducts = async () => {
    const res = await GetProducts({ page: 1, limit: 100 });
    if (res.success && res.data?.length > 0) {
      setProducts(res.data);
      setPreviewProduct(res.data[0]);
    }
  };

  const handleSaveTemplate = async () => {
    if (!activeTemplate.name.trim()) {
      message.error("Template name cannot be empty.");
      return;
    }

    const payload = {
      ...activeTemplate,
      elements: JSON.stringify(activeTemplate.elements),
    };

    if (activeTemplate.id) {
      const res = await UpdateBarcodeTemplate({
        ...payload,
        id: activeTemplate.id,
      });
      if (res.success) {
        message.success("Template updated successfully.");
        fetchTemplates();
      } else {
        message.error(res.message);
      }
    } else {
      const res = await CreateBarcodeTemplate(payload);
      if (res.success) {
        message.success("Template created successfully.");
        setActiveTemplate((prev) => ({ ...prev, id: res.data.id }));
        fetchTemplates();
      } else {
        message.error(res.message);
      }
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    Modal.confirm({
      title: "Delete Template",
      content: "Are you sure you want to delete this barcode template?",
      okText: "Yes, Delete",
      okType: "danger",
      onOk: async () => {
        const res = await DeleteBarcodeTemplate(id);
        if (res.success) {
          message.success("Template deleted successfully.");
          if (activeTemplate.id === id) {
            setActiveTemplate({
              name: "New Label Template",
              width: 50,
              height: 30,
              elements: [],
            });
          }
          fetchTemplates();
        } else {
          message.error(res.message);
        }
      },
    });
  };

  const handleSetDefault = async (id: string) => {
    const res = await SetDefaultBarcodeTemplate(id);
    if (res.success) {
      message.success("Default template set successfully.");
      fetchTemplates();
    } else {
      message.error(res.message);
    }
  };

  const addElement = (type: LabelElement["type"]) => {
    const id = Date.now().toString();
    let newElem: LabelElement = {
      id,
      type,
      x: 10,
      y: 10,
      w:
        type === "barcode"
          ? 140
          : type === "qrcode"
            ? 50
            : type === "line"
              ? 120
              : 120,
      h:
        type === "barcode"
          ? 40
          : type === "qrcode"
            ? 50
            : type === "line"
              ? 3
              : 20,
    };

    if (type === "text") {
      newElem = {
        ...newElem,
        text: "New Text Element",
        fontSize: 10,
        align: "left",
        fontWeight: "normal",
      };
    } else if (type === "barcode") {
      newElem = { ...newElem, barcodeFormat: "code128", text: "{barcode}" };
    } else if (type === "qrcode") {
      newElem = { ...newElem, text: "{barcode}" };
    }

    setActiveTemplate((prev) => ({
      ...prev,
      elements: [...prev.elements, newElem],
    }));
    setSelectedElementId(id);
  };

  const deleteElement = (id: string) => {
    setActiveTemplate((prev) => ({
      ...prev,
      elements: prev.elements.filter((e) => e.id !== id),
    }));
    if (selectedElementId === id) {
      setSelectedElementId(null);
    }
  };

  const updateElementProp = (
    id: string,
    prop: keyof LabelElement,
    val: any,
  ) => {
    setActiveTemplate((prev) => ({
      ...prev,
      elements: prev.elements.map((e) =>
        e.id === id ? { ...e, [prop]: val } : e,
      ),
    }));
  };

  const getElementPreviewText = (text: string) => {
    if (!text) return "";
    let formatted = text;

    if (previewProduct) {
      formatted = formatted.replace(
        /{product_name}/gi,
        previewProduct.title || "Product Name",
      );
      formatted = formatted.replace(
        /{sku}/gi,
        previewProduct.sku_code || "SKU-0000",
      );
      formatted = formatted.replace(
        /{barcode}/gi,
        previewProduct.barcode || "12345678",
      );

      const price = parseFloat(
        previewProduct.mrp || previewProduct.retail_price || 0,
      );
      formatted = formatted.replace(
        /{price}/gi,
        price.toLocaleString(undefined, { minimumFractionDigits: 2 }),
      );

      const cost = parseFloat(previewProduct.cost_price || 0);
      formatted = formatted.replace(
        /{cost_price}/gi,
        cost.toLocaleString(undefined, { minimumFractionDigits: 2 }),
      );
      formatted = formatted.replace(
        /{cost_code}/gi,
        generateCostCode(cost, costCodeAlphabet),
      );
    }

    const expDate = new Date();
    expDate.setDate(expDate.getDate() + expiryDaysOffset);
    const expStr = expDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    formatted = formatted.replace(/{expiry_date}/gi, expStr);

    return formatted;
  };

  const handleDrag = (id: string, e: any, data: any) => {
    const unzoomedX = data.x / zoom;
    const unzoomedY = data.y / zoom;
    const snapGrid = gridSnap ? 5 : 1;
    const newX = Math.round(unzoomedX / snapGrid) * snapGrid;
    const newY = Math.round(unzoomedY / snapGrid) * snapGrid;
    updateElementProp(id, "x", newX);
    updateElementProp(id, "y", newY);
  };

  const selectedElement = activeTemplate.elements.find(
    (e) => e.id === selectedElementId,
  );

  const canvasWidthPx = activeTemplate.width * scale;
  const canvasHeightPx = activeTemplate.height * scale;

  const triggerPrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const labelsHtml = Array.from({ length: printQty })
      .map((_, idx) => {
        return `
          <div class="label-item">
            ${activeTemplate.elements
              .map((el) => {
                const previewText = getElementPreviewText(el.text || "");

                if (el.type === "text") {
                  return `
                  <div style="
                    position: absolute;
                    left: ${el.x}px;
                    top: ${el.y}px;
                    width: ${el.w}px;
                    height: ${el.h}px;
                    font-size: ${el.fontSize}px;
                    font-weight: ${el.fontWeight};
                    text-align: ${el.align};
                    transform: rotate(${el.rotation || 0}deg);
                    white-space: nowrap;
                    overflow: hidden;
                    font-family: 'Inter', sans-serif;
                  ">
                    ${previewText}
                  </div>
                `;
                } else if (el.type === "barcode") {
                  const svgInfo = generateCode128SvgPath(
                    previewText || "12345678",
                  );
                  return `
                  <div style="
                    position: absolute;
                    left: ${el.x}px;
                    top: ${el.y}px;
                    width: ${el.w}px;
                    height: ${el.h}px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                  ">
                    <svg viewBox="0 0 ${svgInfo.width} 100" style="width: 100%; height: 100%;">
                      <path d="${svgInfo.path}" stroke="black" stroke-width="2" fill="none" />
                    </svg>
                  </div>
                `;
                } else if (el.type === "qrcode") {
                  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(previewText || "12345678")}`;
                  return `
                  <img src="${qrUrl}" style="
                    position: absolute;
                    left: ${el.x}px;
                    top: ${el.y}px;
                    width: ${el.w}px;
                    height: ${el.h}px;
                    object-fit: contain;
                  " />
                `;
                } else if (el.type === "logo") {
                  return logoBase64
                    ? `
                  <img src="${logoBase64}" style="
                    position: absolute;
                    left: ${el.x}px;
                    top: ${el.y}px;
                    width: ${el.w}px;
                    height: ${el.h}px;
                    object-fit: contain;
                  " />
                `
                    : `<div style="position: absolute; left: ${el.x}px; top: ${el.y}px; width: ${el.w}px; height: ${el.h}px; border: 1px dashed #ccc;"></div>`;
                } else if (el.type === "line") {
                  return `
                  <div style="
                    position: absolute;
                    left: ${el.x}px;
                    top: ${el.y}px;
                    width: ${el.w}px;
                    height: ${el.h}px;
                    background-color: black;
                  "></div>
                `;
                }
                return "";
              })
              .join("")}
          </div>
        `;
      })
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Labels</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
            body {
              margin: 0;
              padding: 0;
              background-color: white;
            }
            .labels-container {
              display: grid;
              grid-template-columns: repeat(${printCols}, ${canvasWidthPx}px);
              column-gap: ${printColGap}px;
              row-gap: ${printRowGap}px;
              margin-left: ${printMarginLeft}px;
              margin-top: ${printMarginTop}px;
            }
            .label-item {
              position: relative;
              width: ${canvasWidthPx}px;
              height: ${canvasHeightPx}px;
              border: 1px solid #ddd;
              box-sizing: border-box;
              overflow: hidden;
              page-break-inside: avoid;
            }
            @media print {
              .label-item {
                border: none;
              }
              button, .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div style="padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ccc; display: flex; justify-content: space-between;" class="no-print">
            <span>Label Print Sheet Preview</span>
            <button onclick="window.print();" style="padding: 6px 15px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;">
              Print Now
            </button>
          </div>
          <div class="labels-container">
            ${labelsHtml}
          </div>
          <script>
            window.onload = function() {
              setTimeout(() => { window.print(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50 text-gray-800">
      <PageHeader
        title="Barcode & QR Label Editor"
        subText="Design custom print layouts, dynamic tags, ciphers, and saved templates"
        handleButtons={[
          {
            title: "Reload Templates",
            icon: ReloadOutlined,
            onClick: fetchTemplates,
          },
          {
            title: "Save Template",
            icon: SaveOutlined,
            onClick: handleSaveTemplate,
          },
          {
            title: "Print Layout Sheet",
            icon: PrinterOutlined,
            onClick: () => setIsPrintModalOpen(true),
          },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-4">
        {/* Left Column: Templates list & configurations */}
        <div className="lg:col-span-1">
          <TemplatesSidebar
            templates={templates}
            activeTemplate={activeTemplate}
            setActiveTemplate={setActiveTemplate}
            setSelectedElementId={setSelectedElementId}
            handleSetDefault={handleSetDefault}
            handleDeleteTemplate={handleDeleteTemplate}
            products={products}
            previewProduct={previewProduct}
            setPreviewProduct={setPreviewProduct}
            costCodeAlphabet={costCodeAlphabet}
            setCostCodeAlphabet={setCostCodeAlphabet}
            expiryDaysOffset={expiryDaysOffset}
            setExpiryDaysOffset={setExpiryDaysOffset}
          />
        </div>

        {/* Center Column: Label Editor Workspace */}
        <div className="lg:col-span-2">
          <WorkspaceCanvas
            activeTemplate={activeTemplate}
            setActiveTemplate={setActiveTemplate}
            selectedElementId={selectedElementId}
            setSelectedElementId={setSelectedElementId}
            gridSnap={gridSnap}
            setGridSnap={setGridSnap}
            zoom={zoom}
            setZoom={setZoom}
            canvasWidthPx={canvasWidthPx}
            canvasHeightPx={canvasHeightPx}
            addElement={addElement}
            handleDrag={handleDrag}
            getElementPreviewText={getElementPreviewText}
            generateCode128SvgPath={generateCode128SvgPath}
            logoBase64={logoBase64}
            canvasRef={canvasRef}
          />
        </div>

        {/* Right Column: Properties Config panel */}
        <div className="lg:col-span-1">
          <PropertiesSidebar
            selectedElement={selectedElement}
            updateElementProp={updateElementProp}
            deleteElement={deleteElement}
            logoBase64={logoBase64}
            setLogoBase64={setLogoBase64}
          />
        </div>
      </div>

      {/* Print sheet setup dialog */}
      <PrintConfigModal
        isOpen={isPrintModalOpen}
        onCancel={() => setIsPrintModalOpen(false)}
        onOk={() => {
          setIsPrintModalOpen(false);
          triggerPrint();
        }}
        printQty={printQty}
        setPrintQty={setPrintQty}
        printCols={printCols}
        setPrintCols={setPrintCols}
        printMarginTop={printMarginTop}
        setPrintMarginTop={setPrintMarginTop}
        printMarginLeft={printMarginLeft}
        setPrintMarginLeft={setPrintMarginLeft}
        printColGap={printColGap}
        setPrintColGap={setPrintColGap}
        printRowGap={printRowGap}
        setPrintRowGap={setPrintRowGap}
      />
    </div>
  );
}
